import { apiClient, apiClientPaginated } from '@/lib/api-client';

// Menu periods = dayparts (Breakfast / Lunch / Dinner / custom windows). Each
// period has a time window + days and a set of assigned products. Backend:
// services/api/src/menu/menu-periods.controller.ts (mounted under /menus).

export interface MenuPeriod {
  id: string;
  location_id: string;
  name: string;
  /** "HH:mm" or "HH:mm:ss" depending on the DB driver — normalize to HH:mm in the UI. */
  start_time: string;
  end_time: string;
  days_of_week: number[];
  product_ids: string[];
  is_active?: boolean;
}

export interface CreateMenuPeriodPayload {
  location_id: string;
  name: string;
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  days_of_week: number[];
  product_ids: string[];
}

export type UpdateMenuPeriodPayload = Partial<Omit<CreateMenuPeriodPayload, 'location_id'>>;

interface MenuPeriodListResponse {
  data: MenuPeriod[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export const menuPeriodService = {
  // Paginated list — the backend hoists the array to `data` and pagination to
  // `meta`, so use apiClientPaginated (apiClient would return just the array).
  list(locationId?: string): Promise<MenuPeriodListResponse> {
    const q = new URLSearchParams({ limit: '100' });
    if (locationId) q.set('location_id', locationId);
    return apiClientPaginated<MenuPeriod[]>(`/menus/periods?${q.toString()}`);
  },

  create(payload: CreateMenuPeriodPayload): Promise<MenuPeriod> {
    return apiClient<MenuPeriod>('/menus/periods', { method: 'POST', body: payload });
  },

  update(id: string, payload: UpdateMenuPeriodPayload): Promise<MenuPeriod> {
    return apiClient<MenuPeriod>(`/menus/periods/${id}`, { method: 'PATCH', body: payload });
  },

  deactivate(id: string): Promise<{ message?: string }> {
    return apiClient<{ message?: string }>(`/menus/periods/${id}`, { method: 'DELETE' });
  },
};
